import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useOverlayHost } from "./OverlayHost";

const useOverlayBehaviour = (onClose, contained) => {
  const closeRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    if (!contained) {
      closeRef.current?.focus({ preventScroll: true });
      document.body.style.overflow = "hidden";
    }
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, contained]);
  return closeRef;
};

// variant "panel" = right-side panel (desktop), "sheet" = bottom sheet (mobile).
export const DetailOverlay = ({ variant = "panel", onClose, label, testId = "detail-overlay", children }) => {
  const host = useOverlayHost();
  const closeRef = useOverlayBehaviour(onClose, Boolean(host));
  const panel = variant === "panel";

  const node = (
    <div className={cn(host ? "absolute" : "fixed", "pointer-events-auto inset-0 z-50 font-inter")}>
      <div data-testid={`${testId}-backdrop`} onClick={onClose} className="absolute inset-0 bg-os-ink/10 animate-in fade-in-0 duration-200 motion-reduce:animate-none" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        data-testid={testId}
        data-variant={variant}
        className={cn(
          "os-card absolute overflow-y-auto border border-os-border text-os-ink animate-in fade-in-0 duration-200 motion-reduce:animate-none",
          panel
            ? "bottom-3 right-3 top-3 w-[28rem] rounded-card px-7 pb-8 pt-7 slide-in-from-right-3"
            : "inset-x-0 bottom-0 max-h-[88%] rounded-t-card border-b-0 px-5 pb-8 pt-3 slide-in-from-bottom-4",
        )}
      >
        {!panel && <div aria-hidden className="mx-auto mb-3 h-1 w-9 rounded-full bg-os-border-strong" />}
        <button
          ref={closeRef}
          type="button"
          data-testid={`${testId}-close`}
          aria-label="Close"
          onClick={onClose}
          className={cn(
            "absolute inline-flex h-11 w-11 items-center justify-center rounded-control text-os-ink-3 hover:bg-os-wash hover:text-os-ink focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-os-ink",
            panel ? "right-4 top-4" : "right-3 top-4",
          )}
        >
          <X className="h-[1.125rem] w-[1.125rem]" strokeWidth={1.75} />
        </button>
        {children}
      </div>
    </div>
  );

  return createPortal(node, host || document.body);
};
